import Http from "../services/http.service";
import { HttpStatusCode } from 'axios';
import { useUserStore } from '../store/userStore';
import { useHelpers } from './useHelpers';

export const useCompany = () => {

    const userStore = useUserStore();
    const onGetCompany = async () => {
        try {
            const { status, data } = await Http.get('/api/company');
            if(status === HttpStatusCode.Ok){
                // El controller manda { company } DENTRO del envelope
                // ({ success, code, message, data: { company } }) — `data.company`
                // es siempre undefined. Por eso App.vue nunca refrescaba company
                // al boot, y el polling de un plan change nunca veía el plan nuevo
                // aunque el backend ya lo hubiera guardado.
                return data.data.company;
            }
            return false;
        } catch (error) {
            console.log(error);
        }
    }
    
    const onUpdateCompany = async (company) => {
        try {
            const { status, data } = await Http.put('/api/company', company);
            if(status === HttpStatusCode.Ok){
                const company = data.data?.company ?? data.company;
                if (company != null) localStorage.setItem("gs-company", JSON.stringify(company));
                return data.success;
            }
            return false;
        } catch (error) {
            console.log(error);
        }
    }

    const uploadCompanyLogo = async (obj) => {
        try {

            const { data } = await Http.post(`/api/company/upload-logo`, obj, {
                headers: {
                "Content-Type": "multipart/form-data"
                }
            });

            return data;

        } catch (error) {
          console.log('Error updating avatar', error);
        }
    }

    const onGetTransactions = async () => {
        try {
            const { status, data } = await Http.get('/api/transactions');
            if(status === HttpStatusCode.Ok){
                return data.data?.transactions ?? data.transactions ?? null;
            }
            return null;
        } catch (error) {
            console.log(error);
        }
    }

    const onGetSubscriptions = async () => {
        try {
            const { status, data } = await Http.get('/api/company/subscriptions');
            if(status === HttpStatusCode.Ok){
                return data.data.licenses;
            }
            return null;
        } catch (error) {
            console.log(error);
        }
    }

    const onGetCompanyUsers = async () => {
        try {
            const { status, data } = await Http.get('/api/users');
            if(status === HttpStatusCode.Ok){
                return data.data.users;
            }
            return null;
        } catch (error) {
            console.log(error);
        }
    }

    const onGetUserRoles = async () =>{
        try {
            const { status, data } = await Http.get('/api/users/roles');
            if(status === HttpStatusCode.Ok){
                return data.data.roles;
            }
            return null;
        } catch (error) {
            console.log(error);
        }
    }

    const onCreateTeamMember = async (user) => {
        try {
            const { data } = await Http.post('/api/users', user);
            return data;
        } catch (error) {
            console.log(error);
        }
    }

    const onValidEmailAccount = async (email) => {
        try {
            const { status, data } = await Http.post('/api/users/find-user', { email: email});
            if(status === HttpStatusCode.Ok){
                // API shape: { success, code, message, data: { isAvailable } }
                return data?.data?.isAvailable ?? data?.isAvailable ?? false;
            }
            return false;
        } catch (error) {
            console.log(error);
        }
    }

    const onCreatePayment = async (company) => {
        try {
            const { status, data } = await Http.post('/api/transactions/onProcessPayment', company);
            if(status === HttpStatusCode.Ok || status === HttpStatusCode.Created){
                userStore.loadUser(data.data.user);
                userStore.loadCompany(data.data.company);
                return data
            }
            return false;
        } catch (error) {
            console.log(error);
        }
    }

    // Upgrade the LOGGED-IN company's subscription (launchpad → cohort).
    // Unlike onProcessPayment, no user/company is created — the BE resolves
    // the company from the x-token and only charges + flips subscriptionType.
    const onUpgradePayment = async (payload) => {
        try {
            const { status, data } = await Http.post('/api/transactions/onProcessUpgrade', payload);
            if(status === HttpStatusCode.Ok || status === HttpStatusCode.Created){
                if (data?.data?.company) userStore.loadCompany(data.data.company);
                return data
            }
            return false;
        } catch (error) {
            console.log(error);
        }
    }

    // Alta nueva (pública, sin sesión). Devuelve { clientSecret, sessionId }
    // para montar Stripe Checkout embedded — no crea la empresa, eso lo hace
    // el webhook cuando el pago se confirma.
    // Siempre devuelve el envelope completo (con `code` incluido en error) —
    // el caller necesita distinguir MISSING_FIELDS de RATE_LIMITED, no solo
    // saber que falló.
    const onCheckoutSignup = async (payload) => {
        try {
            const { data } = await Http.post('/api/transactions/checkout-signup', payload);
            return data;
        } catch (error) {
            console.log(error);
            return { success: false, code: null };
        }
    }

    // Renovar / cambiar de plan (empresa autenticada). La respuesta viene
    // discriminada por `mode`: 'redirect' trae clientSecret para montar el
    // checkout embedded, 'updated' significa que ya se aplicó (sin checkout).
    // Mismo criterio que onCheckoutSignup: siempre el envelope completo,
    // el `code` de un 409 (DOWNGRADE_TO_FLOOR, SAME_PLAN, etc.) es lo que
    // el caller necesita para mostrar el mensaje correcto.
    const onCreateCheckoutSession = async ({ product, interval }) => {
        try {
            const { data } = await Http.post('/api/transactions/checkout-session', { product, interval });
            return data;
        } catch (error) {
            console.log(error);
            return { success: false, code: null };
        }
    }

    // Estado de un alta nueva mientras el webhook crea la empresa. Pública.
    const onGetCheckoutStatus = async (sessionId) => {
        try {
            const { status, data } = await Http.get('/api/transactions/checkout-status', { params: { sessionId } });
            if(status === HttpStatusCode.Ok){
                return data?.data ?? data;
            }
            return null;
        } catch (error) {
            console.log(error);
            return null;
        }
    }

    // Portal de facturación de Stripe (cambiar tarjeta, ver facturas, cancelar).
    // Los 409 (sin plan, sede, o facturado fuera de Stripe) traen un `code`
    // propio que el caller necesita para mostrar el mensaje correcto — por
    // eso acá SIEMPRE se devuelve el `code`, no solo `false` en error.
    const onGetBillingPortal = async () => {
        try {
            const { status, data } = await Http.post('/api/transactions/billing-portal');
            if(status === HttpStatusCode.Ok){
                return { url: data?.data?.url ?? data?.url };
            }
            return { code: data?.code };
        } catch (error) {
            // Axios rechaza en un 409 — el `code` vive en error.response.data,
            // no en `data` de arriba. Devolver null acá tapaba siempre el
            // mensaje específico (NO_SUBSCRIPTION, BILLING_ON_PARENT, etc.)
            // detrás del genérico.
            return { code: error?.response?.data?.code ?? null };
        }
    }

    // Próxima factura real, o simulada si se pasan product+interval (docs/
    // subscription-billing-fe-contract.md §6b) — antes de confirmar un cambio
    // de plan, para no sorprender con el prorrateo (ver el ejemplo del
    // contrato: Core→Cohort el mismo día da 1648.48, no el precio del plan).
    // `data: null` es válido (no hay nada por cobrarse, ej. pago único) y no
    // es un error.
    const onGetUpcomingInvoice = async (product = null, interval = null) => {
        try {
            const params = {};
            if (product) params.product = product;
            if (interval) params.interval = interval;
            const { status, data } = await Http.get('/api/transactions/upcoming-invoice', { params });
            if(status === HttpStatusCode.Ok){
                return { data: data?.data ?? null };
            }
            return { code: data?.code };
        } catch (error) {
            return { code: error?.response?.data?.code ?? null };
        }
    }

    // Historial de pagos, ya resuelto (nombre del producto, invoice, etc.) —
    // reemplaza a onGetTransactions, que trae las filas crudas con stripeResponse.
    const onGetPaymentHistory = async () => {
        try {
            const { status, data } = await Http.get('/api/transactions/history');
            if(status === HttpStatusCode.Ok){
                return data?.data?.payments ?? [];
            }
            return [];
        } catch (error) {
            console.log(error);
            return [];
        }
    }

    // Catálogo público de productos con precio (docs/subscription-billing-fe-
    // contract.md §3) — pública, no requiere sesión, pero vive acá junto al
    // resto de las funciones de facturación.
    const onGetSalesProducts = async () => {
        try {
            const { status, data } = await Http.get('/api/sales-products');
            if(status === HttpStatusCode.Ok){
                return data?.data ?? [];
            }
            return [];
        } catch (error) {
            console.log(error);
            return [];
        }
    }

    const onHandleUserStatus = async (obj) => {
        try {
            const { status, data } = await Http.put('/api/users/update-status', obj);
            if(status === HttpStatusCode.Ok){
                return data.success
            }
            return false;
        } catch (error) {
            console.log(error);
        }
    }

    const onUpdateCompanyNotifications = async (obj) => {
        try {
            const { status } = await Http.post('/api/company/notifications', obj);
            if(status === HttpStatusCode.Ok){
                return true;
            }
            return false;
        } catch (error) {
            console.log(error);
        }
    }

    const onGetCompanyNotifications = async () => {
        try {
            const { status, data } = await Http.get('/api/company/notifications');
            if(status === HttpStatusCode.Ok){
                return data.notifications;
            }
            return null;
        } catch (error) {
            console.log(error);
        }
    }

    const onGetReportCompany = async (obj) => {
        try {
            const { status, data } = await Http.post('/api/reports/metrics', obj);
            if(status === HttpStatusCode.Ok){
                return data;
            }
            return null;
        } catch (error) {
            console.log(error);
        }
    }

    const onGenerateInsightsAI = async (obj) => {
        try {
            const { status, data } = await Http.post('/api/reports/generateInsights', obj);
            if(status === HttpStatusCode.Ok){
                return data;
            }
            return null;
        } catch (error) {
            console.log(error);
        }
    }

    const getAllSharedLinks = async () => {
        try {
            const { status, data } = await Http.get('/api/reports/getAllSharedLinks');
            if(status === HttpStatusCode.Ok){
                return data.data;
            }
            return null;
        } catch (error) {
            console.log(error);
        }
    }

    

     const onGetSharelinkInfo = async (uuid) => {
        try {
            const { status, data } = await Http.get(`/api/company/getSharelinkInfo/${uuid}`);
            if (status !== HttpStatusCode.Ok) {
             return null;
            }
            return data;
          } catch (error) {
            console.log('Error saving data FR', error);
        }
    }

    const fetchCertifications = async (companyId = null) => {
        try {
            const params = companyId ? { companyId } : {};
            const { data } = await Http.get('/api/company/certifications', { params });
            const records = data?.data?.records ?? [];
            return records.map(r => ({
                name: r.certification?.replace(/_/g, ' ') ?? '',
                year: r.issuedDate ? new Date(r.issuedDate).getFullYear() : null,
            }));
        } catch (error) {
            console.error('[fetchCertifications] error:', error);
            return [];
        }
    };

    return{
        onGetCompany,
        onUpdateCompany,
        uploadCompanyLogo,
        onGetTransactions,
        onGetReportCompany,
        onGetSubscriptions,
        onGetCompanyUsers,
        onGetUserRoles,
        onCreateTeamMember,
        onValidEmailAccount,
        onCreatePayment,
        onUpgradePayment,
        onCheckoutSignup,
        onCreateCheckoutSession,
        onGetCheckoutStatus,
        onGetBillingPortal,
        onGetPaymentHistory,
        onGetUpcomingInvoice,
        onGetSalesProducts,
        onGetSharelinkInfo,
        onHandleUserStatus,
        onGenerateInsightsAI,
        getAllSharedLinks,
        onGetCompanyNotifications,
        onUpdateCompanyNotifications,
        fetchCertifications,
        searchAddress,
        fetchPublicProfile,
        fetchPublicCertificationProgress,
        fetchCategoryMetrics,
        fetchPublicInitiativeKPIs,
        fetchPublicEnvironmentalKPIs,
        fetchPublicStaffKPIs,
        fetchPublicSuppliersKPIs,
        fetchPublicGovernanceKPIs,
        fetchPublicInitiatives,
        fetchPublicCulturalAssets,
        fetchPublicOngoingTasks,
        fetchPublicCertifications,
        fetchPublicReports,
        fetchPublicCustomReports,
        openPublicCustomReport,
        fetchPublicTours,
        fetchPublicTourDetail,
        fetchPublicTourMonitor,
        fetchPublicProtocol,
        fetchPublicProtocols,
        fetchPublicPolicies,
    }
}

const fetchCategoryMetrics = async (uuid, lang = 'en') => {
    try {
        const response = await Http.get('/api/actionplan/tasks/metrics/categories', {
            params: { uuid, lang },
            headers: { 'x-secret': process.env.VUE_APP_PROFILE_SECRET },
        });
        return response.data?.data ?? null;
    } catch {
        return null;
    }
};

const fetchPublicProfile = async (uuid) => {
    try {
        const response = await Http.get('/api/company/profile', {
            params: { uuid },
            headers: { 'x-secret': process.env.VUE_APP_PROFILE_SECRET },
        });
        return response.data?.data ?? null;
    } catch {
        return null;
    }
};

// Progreso hacia la certificación (mismo cálculo que el dashboard: assessment
// enviado + avance del action plan). Público por uuid. Devuelve null si el
// endpoint aún no existe → el landing cae al Sustainability Score.
// Spec BE: docs/certification-progress-be-spec.md
const fetchPublicCertificationProgress = async (uuid) => {
    try {
        const { data } = await Http.get('/api/assestments/certification-progress', {
            params: { uuid },
            headers: { 'x-secret': process.env.VUE_APP_PROFILE_SECRET },
        });
        return data?.data ?? data ?? null; // { percent, label }
    } catch {
        return null;
    }
};

const searchAddress = async (text) => {
    try {
        const language = JSON.parse(localStorage.getItem('gs_lang')) || 'en';
        const response = await Http.get('/api/places/search', {
            params: { query: text, language },
            headers: { 'x-secret': process.env.VUE_APP_PLACES_SECRET },
        });
        return response.data?.data?.places ?? [];
    } catch {
        return [];
    }
};

const _pubSecret = () => ({ 'x-secret': process.env.VUE_APP_PROFILE_SECRET });
const _pubUnwrap = (res) => res.data?.data ?? res.data ?? null;

const fetchPublicInitiativeKPIs = async (uuid, params = {}) => {
    try {
        const res = await Http.get('/api/monitor/social-initiatives/kpis', { params: { uuid, ...params }, headers: _pubSecret() });
        return _pubUnwrap(res);
    } catch { return null; }
};

const fetchPublicEnvironmentalKPIs = async (uuid, params = {}) => {
    try {
        const res = await Http.get('/api/monitor/environmental/kpis', { params: { uuid, ...params }, headers: _pubSecret() });
        return _pubUnwrap(res);
    } catch { return null; }
};

const fetchPublicStaffKPIs = async (uuid, params = {}) => {
    try {
        const res = await Http.get('/api/monitor/staff/kpis', { params: { uuid, ...params }, headers: _pubSecret() });
        return _pubUnwrap(res);
    } catch { return null; }
};

const fetchPublicSuppliersKPIs = async (uuid, params = {}) => {
    try {
        const res = await Http.get('/api/monitor/suppliers/kpis', { params: { uuid, ...params }, headers: _pubSecret() });
        return _pubUnwrap(res);
    } catch { return null; }
};

const fetchPublicGovernanceKPIs = async (uuid, params = {}) => {
    try {
        const res = await Http.get('/api/monitor/governance/kpis', { params: { uuid, ...params }, headers: _pubSecret() });
        return _pubUnwrap(res);
    } catch { return null; }
};

const _getCertLogo = (certification) => {
    if (!certification) return null;
    const { getIconPath } = useHelpers();
    return getIconPath(`${certification}.svg`, 'images/certifications') ?? null;
};

const fetchPublicCertifications = async (uuid) => {
    try {
        const res = await Http.get('/api/company/certifications', {
            params: { uuid },
            headers: _pubSecret(),
        });
        const records = res.data?.data?.records ?? [];
        return records.map(r => {
            const rawDate = r.expirationDate ?? r.expiresAt ?? r.issuedDate ?? null;
            const date = rawDate
                ? new Date(rawDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
                : '';
            return {
                name:           r.certification?.replace(/_/g, ' ') ?? '',
                logo:           _getCertLogo(r.certification),
                descriptionKey: r.certification ? `certifications.descriptions.${r.certification}` : '',
                date,
                status:         r.status ?? 'verified',
            };
        });
    } catch { return []; }
};

const fetchPublicReports = async (uuid) => {
    try {
        const res = await Http.get('/api/reports/getAllSharedLinks', {
            params: { uuid },
            headers: _pubSecret(),
        });
        const records = res.data?.data?.records ?? [];
        return records
            .filter(r => !r.revoked)
            .map(r => ({
                id:          r.id,
                name:        r.name || (r.product ? r.product.charAt(0).toUpperCase() + r.product.slice(1) : ''),
                year:        r.createdAt ? new Date(r.createdAt).getFullYear() : '',
                type:        r.product   ? r.product.charAt(0).toUpperCase() + r.product.slice(1) : '',
                product:     r.product || null,
                createdAt:   r.createdAt || null,
                viewUrl:     `/shared-env/${r.uuid}`,
                downloadUrl: null,
            }));
    } catch { return []; }
};

/**
 * Reportes externos (PDF o link subidos en el Report Hub) que se muestran en el
 * perfil público. Respeta `showReports` en el BE. Solo visibles, sin autor.
 */
const fetchPublicCustomReports = async (uuid) => {
    try {
        const res = await Http.get('/api/custom-reports/public', { params: { uuid }, headers: _pubSecret() });
        return _pubUnwrap(res)?.items ?? [];
    } catch { return []; }
};

/** Link para abrir un reporte externo: el del PDF vence en 15 min, se pide al hacer clic. */
const openPublicCustomReport = async (uuid, reportId) => {
    try {
        const res = await Http.get(`/api/custom-reports/public/${reportId}/open`, { params: { uuid }, headers: _pubSecret() });
        return _pubUnwrap(res)?.url || null;
    } catch { return null; }
};

const fetchPublicInitiatives = async (uuid, params = {}) => {
    try {
        const res = await Http.get('/api/monitor/social-initiatives', {
            params: { uuid, ...params },
            headers: _pubSecret(),
        });
        const payload = _pubUnwrap(res);
        return {
            records:    payload?.records    ?? [],
            pagination: payload?.pagination ?? null,
        };
    } catch { return { records: [], pagination: null }; }
};

// Atractivos y elementos culturales públicos (mismo endpoint que el Monitor,
// leído por uuid con el secreto público). → { records, pagination }.
const fetchPublicCulturalAssets = async (uuid, params = {}) => {
    try {
        const res = await Http.get('/api/monitor/cultural-assets', {
            params: { uuid, ...params },
            headers: _pubSecret(),
        });
        const payload = _pubUnwrap(res);
        return {
            records:    payload?.records    ?? [],
            pagination: payload?.pagination ?? null,
        };
    } catch { return { records: [], pagination: null }; }
};

// Tareas en progreso del plan de acción (público por uuid). El BE devuelve
// `data` como array plano → [{ id, titleEn, titleEs }]. `limit` default 6.
const fetchPublicOngoingTasks = async (uuid, limit = 6) => {
    try {
        const res = await Http.get('/api/actionplan/ongoing-tasks', {
            params: { uuid, limit },
            headers: _pubSecret(),
        });
        const payload = _pubUnwrap(res);
        return Array.isArray(payload) ? payload : (payload?.data ?? []);
    } catch { return []; }
};

// Catálogo público de tours/experiencias (inventoryActivities activas).
// → [{ id, name, desc, coverPhoto, detailAvailable }]. 404 si el perfil es
// privado / showTours off.
const fetchPublicTours = async (uuid) => {
    try {
        const res = await Http.get('/api/company/tours', { params: { uuid }, headers: _pubSecret() });
        return res.data?.data?.tours ?? [];
    } catch { return []; }
};

// Detalle público de un tour (Ficha de Tour Responsable, solo la parte de
// oferta comercial) — fotos, descripción, incluye/excluye, duración, tamaño
// de grupo y accesibilidad. `null` si no existe, no es de esta empresa, o su
// ficha no pasó la revisión de publicación (mismo 404 en los tres casos).
const fetchPublicTourDetail = async (uuid, id) => {
    try {
        const res = await Http.get(`/api/company/tours/${id}`, { params: { uuid }, headers: _pubSecret() });
        return res.data?.data?.tour ?? null;
    } catch { return null; }
};

// Consumo real de Monitor para ESTE tour (energía/combustible/residuos/aguas
// residuales/fugas + CO2e total) — `null` si no hay registros cargados
// todavía, que no es un error.
const fetchPublicTourMonitor = async (uuid, id) => {
    try {
        const res = await Http.get(`/api/company/tours/${id}/monitor`, { params: { uuid }, headers: _pubSecret() });
        return res.data?.data?.monitor ?? null;
    } catch { return null; }
};

// Un protocolo completo — cuerpo redactado (contentMode 'ai') o archivo
// adjunto (contentMode 'file') — para abrirlo desde los "Protocolos ligados"
// de un tour, o desde el listado de políticas del perfil público. `null` si
// no existe, es de otra empresa, o no está publicado.
const fetchPublicProtocol = async (uuid, id) => {
    try {
        const res = await Http.get(`/api/company-protocols/public/${id}`, { params: { uuid }, headers: _pubSecret() });
        return res.data?.data?.protocol ?? null;
    } catch { return null; }
};

// Todos los protocolos publicados de la empresa — la página
// /sustainability/:uuid/protocols. Mismo endpoint que alimenta la sección
// "Protocolos" de un tour, pero sin filtrar por actividad.
const fetchPublicProtocols = async (uuid) => {
    try {
        const res = await Http.get('/api/company-protocols/public', { params: { uuid }, headers: _pubSecret() });
        return res.data?.data?.protocols ?? [];
    } catch { return []; }
};

// Políticas públicas = publicaciones del Communication Hub external+published.
// → [{ id, title:{en,es}, body:{en,es} HTML, category, publishedAt }].
const fetchPublicPolicies = async (uuid) => {
    try {
        const res = await Http.get('/api/communication/policies', { params: { uuid }, headers: _pubSecret() });
        return res.data?.policies ?? [];
    } catch { return []; }
};